/**
 * Service Worker 辅助工具
 * 用于强制更新和清理旧的 Service Worker
 */

class ServiceWorkerHelper {
  /**
   * 强制更新 Service Worker
   */
  static async forceUpdate() {
    if (!('serviceWorker' in navigator)) {
      console.warn('浏览器不支持 Service Worker')
      return false
    }

    try {
      console.log('🔄 开始强制更新 Service Worker...')
      
      // 1. 获取所有注册的 Service Worker
      const registrations = await navigator.serviceWorker.getRegistrations()
      console.log(`找到 ${registrations.length} 个 Service Worker`)
      
      // 2. 注销所有旧的 Service Worker
      for (const registration of registrations) {
        await registration.unregister()
        console.log('✅ 已注销旧的 Service Worker')
      }
      
      // 3. 清除所有缓存
      const cacheNames = await caches.keys()
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName)
        console.log('✅ 已清除缓存:', cacheName)
      }
      
      // 4. 重新注册新的 Service Worker（强制更新，不缓存）
      const newRegistration = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none', // 不缓存 Service Worker 文件
        scope: '/' // 明确指定作用域
      })
      console.log('✅ 新的 Service Worker 已注册')
      
      // 5. 立即触发更新检查
      await newRegistration.update()
      
      // 6. 处理等待中的 Service Worker
      if (newRegistration.waiting) {
        console.log('🔄 发现等待中的 Service Worker，立即激活')
        newRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
        // 等待激活完成
        await new Promise((resolve) => {
          const checkActivated = () => {
            if (newRegistration.active && newRegistration.active.state === 'activated') {
              console.log('✅ Service Worker 已激活')
              resolve()
            } else {
              setTimeout(checkActivated, 100)
            }
          }
          checkActivated()
        })
      }
      
      // 7. 监听安装和激活事件
      if (newRegistration.installing) {
        newRegistration.installing.addEventListener('statechange', () => {
          const state = newRegistration.installing?.state
          console.log('🔄 Service Worker 状态变化:', state)
          if (state === 'installed') {
            console.log('✅ Service Worker 已安装，准备激活')
            if (newRegistration.waiting) {
              newRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
            }
          } else if (state === 'activated') {
            console.log('✅ Service Worker 已激活')
          }
        })
      }
      
      // 8. 等待一小段时间确保激活完成
      await new Promise(resolve => setTimeout(resolve, 500))
      
      console.log('✅ Service Worker 强制更新完成')
      return true
    } catch (error) {
      console.error('❌ Service Worker 强制更新失败:', error)
      return false
    }
  }

  /**
   * 检查 Service Worker 状态
   */
  static async checkStatus() {
    if (!('serviceWorker' in navigator)) {
      return {
        supported: false,
        registered: false,
        active: false,
        waiting: false
      }
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      return {
        supported: true,
        registered: !!registration,
        active: !!registration?.active,
        waiting: !!registration?.waiting,
        registration
      }
    } catch (error) {
      console.error('检查 Service Worker 状态失败:', error)
      return {
        supported: true,
        registered: false,
        active: false,
        waiting: false,
        error: error.message
      }
    }
  }

  /**
   * 清除所有缓存
   */
  static async clearAllCaches() {
    try {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map(name => caches.delete(name)))
      console.log('✅ 所有缓存已清除')
      return true
    } catch (error) {
      console.error('❌ 清除缓存失败:', error)
      return false
    }
  }

  /**
   * 在控制台显示更新提示
   */
  static showUpdateInstructions() {
    console.log('%c🔄 Service Worker 更新指南', 'color: #667eea; font-size: 18px; font-weight: bold;')
    console.log('%c如果遇到图片加载问题，请尝试以下方法：', 'color: #764ba2; font-size: 14px;')
    console.log('%c1. 刷新页面（F5 或 Ctrl+R）', 'color: #333; font-size: 12px;')
    console.log('%c2. 强制刷新（Ctrl+Shift+R 或 Cmd+Shift+R）', 'color: #333; font-size: 12px;')
    console.log('%c3. 运行强制更新：', 'color: #333; font-size: 12px;')
    console.log('%c   window.updateServiceWorker()', 'color: #667eea; font-size: 12px; background: #f0f0f0; padding: 4px 8px; border-radius: 4px;')
  }
}

// 暴露到全局，方便用户在控制台调用
if (typeof window !== 'undefined') {
  window.updateServiceWorker = async () => {
    const success = await ServiceWorkerHelper.forceUpdate()
    if (success) {
      console.log('✅ 更新成功！刷新页面以应用更改...')
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } else {
      console.error('❌ 更新失败，请手动刷新页面')
    }
  }

  window.checkServiceWorker = async () => {
    const status = await ServiceWorkerHelper.checkStatus()
    console.log('Service Worker 状态:', status)
    return status
  }

  window.clearCaches = async () => {
    const success = await ServiceWorkerHelper.clearAllCaches()
    if (success) {
      console.log('✅ 缓存已清除，请刷新页面')
    }
    return success
  }

  // 页面加载时显示更新指南
  window.addEventListener('load', () => {
    setTimeout(() => {
      ServiceWorkerHelper.showUpdateInstructions()
    }, 2000)
  })
}

export default ServiceWorkerHelper

